import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Search, User, Check, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

interface Patient {
  id: string
  full_name: string
  email: string
  phone?: string
  date_of_birth?: string
}

interface PatientSelectorProps {
  selectedPatientId?: string
  onPatientSelect: (patient: Patient | null) => void
  disabled?: boolean
  required?: boolean
  placeholder?: string
}

export default function PatientSelector({
  selectedPatientId,
  onPatientSelect,
  disabled = false,
  required = false,
  placeholder = "Buscar paciente por nome ou email..."
}: PatientSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [patients, setPatients] = useState<Patient[]>([])
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Carregar paciente selecionado inicialmente
  useEffect(() => {
    if (selectedPatientId && !selectedPatient) {
      loadSelectedPatient(selectedPatientId)
    }
  }, [selectedPatientId])

  const loadSelectedPatient = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, date_of_birth')
        .eq('id', patientId)
        .eq('role', 'patient')
        .single()

      if (error) {
        console.error('Erro ao carregar paciente:', error)
        setError('Erro ao carregar dados do paciente')
        return
      }

      if (data) {
        const patient: Patient = {
          id: data.id,
          full_name: data.full_name || 'Nome não informado',
          email: data.email || '',
          phone: data.phone,
          date_of_birth: data.date_of_birth
        }
        setSelectedPatient(patient)
        setSearchTerm(patient.full_name)
      }
    } catch (error) {
      console.error('Erro ao carregar paciente:', error)
      setError('Erro ao carregar dados do paciente')
    }
  }

  const searchPatients = async (term: string) => {
    if (!term.trim() || term.length < 2) {
      setPatients([])
      setShowDropdown(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, date_of_birth')
        .eq('role', 'patient')
        .or(`full_name.ilike.%${term}%,email.ilike.%${term}%`)
        .limit(10)

      if (error) {
        console.error('Erro ao buscar pacientes:', error)
        setError('Erro ao buscar pacientes')
        setPatients([])
        return
      }

      const formattedPatients: Patient[] = (data || []).map(patient => ({
        id: patient.id,
        full_name: patient.full_name || 'Nome não informado',
        email: patient.email || '',
        phone: patient.phone,
        date_of_birth: patient.date_of_birth
      }))

      setPatients(formattedPatients)
      setShowDropdown(true)
    } catch (error) {
      console.error('Erro ao buscar pacientes:', error)
      setError('Erro ao buscar pacientes')
      setPatients([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    
    // Se limpar o campo, limpar seleção
    if (!value.trim()) {
      setSelectedPatient(null)
      onPatientSelect(null)
      setPatients([])
      setShowDropdown(false)
      return
    }

    // Buscar pacientes com debounce
    const timeoutId = setTimeout(() => {
      searchPatients(value)
    }, 300)

    return () => clearTimeout(timeoutId)
  }

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient)
    setSearchTerm(patient.full_name)
    setShowDropdown(false)
    setPatients([])
    setError(null)
    onPatientSelect(patient)
  }

  const formatPatientInfo = (patient: Patient) => {
    const parts = [patient.full_name]
    if (patient.email) parts.push(patient.email)
    if (patient.date_of_birth) {
      const age = new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear()
      parts.push(`${age} anos`)
    }
    return parts.join(' • ')
  }

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Paciente {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => {
            if (patients.length > 0) setShowDropdown(true)
          }}
          onBlur={() => {
            // Delay para permitir clique nos resultados
            setTimeout(() => setShowDropdown(false), 200)
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          {loading ? (
            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
          ) : selectedPatient ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : error ? (
            <AlertCircle className="h-4 w-4 text-red-500" />
          ) : null}
        </div>
      </div>

      {/* Dropdown com resultados */}
      {showDropdown && patients.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {patients.map((patient) => (
            <div
              key={patient.id}
              onClick={() => handlePatientSelect(patient)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {patient.full_name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {formatPatientInfo(patient)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paciente selecionado */}
      {selectedPatient && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Paciente selecionado: {selectedPatient.full_name}
              </p>
              <p className="text-xs text-blue-700">
                {selectedPatient.email}
                {selectedPatient.date_of_birth && (
                  <span className="ml-2">
                    • {new Date().getFullYear() - new Date(selectedPatient.date_of_birth).getFullYear()} anos
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mensagem de erro */}
      {error && (
        <div className="mt-2 text-sm text-red-600 flex items-center space-x-1">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Mensagem de validação */}
      {required && !selectedPatient && (
        <div className="mt-2 text-sm text-gray-500">
          Selecione um paciente para continuar
        </div>
      )}
    </div>
  )
}